import { motion, type Variants } from 'motion/react';
import { Link } from 'react-router-dom';
import { Container, DimensionMark, Mark } from './primitives';
import { ProductMockup } from './ProductMockup';
import { Button } from './ui/button';

const EASE = [0.22, 1, 0.36, 1] as const;

const STAGGER: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
};

const ITEM: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE },
  },
};

export function Hero() {
  return (
    <section
      id="top"
      className="relative isolate overflow-hidden pt-36 pb-16 md:pt-44 md:pb-24"
    >
      {/* Decorative gradient */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(60% 50% at 50% -10%, color-mix(in oklch, var(--primary) 22%, transparent), transparent 70%), radial-gradient(40% 30% at 80% 10%, color-mix(in oklch, var(--brand-accent) 14%, transparent), transparent 70%)',
        }}
      />
      {/* Blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 blueprint-grid opacity-[0.05]"
        style={{
          maskImage: 'radial-gradient(60% 50% at 50% 0%, black, transparent 75%)',
        }}
      />

      <Container className="relative">
        <DimensionMark
          value="42.464N · 14.214E · Abruzzo"
          className="absolute -left-2 top-4 hidden origin-left rotate-90 lg:flex"
        />
        <DimensionMark
          value="release · 3.0 · pilota"
          className="absolute -right-2 top-4 hidden origin-right rotate-90 lg:flex"
        />

        <motion.div
          variants={STAGGER}
          initial="hidden"
          animate="show"
          className="flex flex-col items-center text-center"
        >
          <motion.h1
            variants={ITEM}
            className="boss-display mt-8 text-balance text-[64px] md:text-[112px]"
          >
            La commessa, <br className="hidden md:block" />
            <Mark>sotto controllo.</Mark>
          </motion.h1>

          <motion.p
            variants={ITEM}
            className="mt-7 max-w-[680px] text-pretty text-[17px] leading-[1.55] text-fg-2"
          >
            BOSS è il gestionale della commessa per le imprese di costruzioni: segue lavori, costi e persone,
            automatizza i processi ripetitivi e ti mostra la contabilità analitica di ogni cantiere mentre
            lavora —{' '}
            <em className="not-italic text-foreground">dal rapportino di oggi alla busta paga di fine mese</em>.
            Al posto dei gruppi WhatsApp, degli Excel sparsi e dei PDF dimenticati.
          </motion.p>

          <motion.div variants={ITEM} className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" variant="default">
              <a href="#demo">
                Prenota una demo <span aria-hidden>→</span>
              </a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="#funzionalita">Scopri cosa fa</a>
            </Button>
          </motion.div>

          <motion.div variants={ITEM} className="mt-6">
            <Link
              to="/funzionalita/presenze-buste-paga"
              className="group inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-[13px] text-foreground transition-colors hover:bg-primary/20"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary">Novità</span>
              Buste paga edili e metalmeccanici: il file per il consulente in un clic
              <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </motion.div>

          <motion.div
            variants={ITEM}
            className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
          >
            Già al lavoro · su cantieri veri · disponibile ad altre imprese da gennaio 2027
          </motion.div>
        </motion.div>

        <div className="relative mt-20 md:mt-24">
          <ProductMockup />

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <span>fig. 01 — sala operativa · vista cantieri · dati di esempio</span>
            <span>boss · build 2026.10</span>
          </div>
        </div>
      </Container>
    </section>
  );
}
